import {Injectable} from '@angular/core';
import {ContainerService} from '../../Utils/container.service';
import {RenderService} from './render.service';
import {
    BufferGeometry,
    DoubleSide,
    Geometry,
    LoadingManager,
    Mesh,
    MeshBasicMaterial,
    MeshPhongMaterial,
    NearestFilter,
    Object3D,
    Texture,
    TextureLoader,
    Vector3,
    Group,
} from 'three';
import {Subject} from 'rxjs';
import {ObjectUtilsService} from '../object-utils.service';
import {TextureSizeService} from '../Calculation/texture-size.service';
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader';
import {BufferGeometryUtils} from 'three/examples/jsm/utils/BufferGeometryUtils';
import {ModelDataService} from '../../Utils/model-data.service';

@Injectable({
    providedIn: 'root'
})
export class LoaderService {

    public loaded = new Subject<number>();
    public faceNeighboursComputation = false;

    constructor(private containerService: ContainerService,
                private renderService: RenderService,
                private modelOperationsService: ObjectUtilsService,
                private textureSizeService: TextureSizeService,
                private modelDataService: ModelDataService
    ) {
    }

    private _singelton = false;

    get singelton(): boolean {
        return this._singelton;
    }

    set singelton(value: boolean) {
        this._singelton = value;
    }

    public initLoaderService(reload = true) {
        this.containerService.renderGroup = new Object3D();
        this.containerService.polyPaintGroup = new Object3D();
        this.containerService.renderGroup.name = 'RenderGroup';
        this.containerService.polyPaintGroup.name = 'PolyGroup';
        this.initModel(reload);
    }

    // public scaleModel(object: any) {
    //     // const factor = this.modelOperationsService.scaleMesh(object);
    //     // this.containerService.scalefactor = factor;
    // }

    /**
     * load gltf file from url and sets uvs.
     * load state -1 = error, 0 = all okay, 1 = finished computeTargetOffsets, 2 = finished computeFaceNeighbours
     * maxStatus 0.9 --> some further computations are needed
     * maxStatus 1 --> no further computations are needed (finish when file is loaded)
     * @param url
     * @param uvs
     * @param maxStatus
     */
    public loadGltfModelFromUrl(url: string, uvs = null, maxStatus = 0.9) {
        const manager = new LoadingManager();
        manager.onStart = (item, itemsLoaded, itemsTotal) => {
            this.containerService.loadingStatus = 0;
        };

        manager.onProgress = (item, itemsLoaded, itemsTotal) => {
            this.containerService.loadingStatus = itemsLoaded / (itemsTotal + 1);
        };

        manager.onLoad = () => {
            this.containerService.loadingStatus = maxStatus;
        };

        manager.onError = (item) => {
            console.log('There was an error loading ' + item);
        };

        // Instantiate a loader
        const loader = new GLTFLoader(manager);
        // Load a glTF resource
        loader.load(
            // resource URL
            url,
            // called when the resource is loaded
            (gltf) => {
                // // extract model name from url
                let modelName = url.split('/').pop(); //  'cube3072';
                modelName = modelName.substring(0, modelName.length - 5);

                const object = this.modelOperationsService.getMeshWithName(gltf.scene, modelName);
                const geometry = this.getGeometry(object);
                this.addModel(geometry);

                // // this._executedWebWorkderTasks = 0;

                this.modelOperationsService.updateVertices([
                        <Mesh>this.containerService.renderGroup.children[0],
                        <Mesh>this.containerService.polyPaintGroup.children[0]
                    ],
                    this.containerService.targets,
                    this.containerService.targetInfluences);

                this.textureSizeService.initArea(<Mesh>this.containerService.renderGroup.children[0]);
                if (maxStatus < 1) {
                    this.computeFaceNeighbours(<Geometry>(<Mesh>this.containerService.polyPaintGroup.children[0]).geometry);
                    this.textureSizeService.mapTexelToFace(<Mesh>this.containerService.renderGroup.children[0],
                                                           this.containerService.textureCanvas.cloneNode());
                }
                const geo = <Geometry>(<Mesh>this.containerService.polyPaintGroup.children[0]).geometry;

                // faces changed -> update face neighbours
                if (uvs == null || uvs.length !== geo.faceVertexUvs[0].length) {
                    this.computeFaceNeighbours(geo);
                }

                // apply rotation and translation AFTER! morph Targets
                this.loaded.next(0);
                // only render on initial load of program
                // if model is removed and newly loaded (male/female change) hide model
                if (uvs === null) {
                    this.renderService.render();
                }
            },


            // called while loading is progressing
            (xhr) => {
                console.log((xhr.loaded / xhr.total * 100) + '% loaded');
            },
            // called when loading has errors
            (error) => {
                console.log(error);
                this.loaded.next(-1);
            }
        );
    }

    public removeCurrentModel() {
        const ppg = this.containerService.polyPaintGroup;
        ppg.remove(ppg.children[ppg.children.length - 1]);

        const rg = this.containerService.renderGroup;
        const model = <Mesh>rg.children[rg.children.length - 1];
        const uvs = (<Geometry>model.geometry).faceVertexUvs[0];
        rg.remove(rg.children[rg.children.length - 1]);

        this.renderService.render();
        return uvs;
    }


    public loadTexure() {
        const img = new Image();
        img.src = './assets/Human/Textures/skin.png';
        const ctx = this.containerService.textureCanvas.getContext('2d');

        img.onload = () => {
            this.containerService.textureCanvas.width = img.width;
            this.containerService.textureCanvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            // this.containerService.texture.magFilter = NearestFilter;
            // this.containerService.texture.minFilter = NearestFilter;
            this.containerService.texture.needsUpdate = true;
            this.renderService.render();
        };
    }


    // -------------------------------------------------------------------------------------------------------------------------------------

    private getGeometry(object) {
        let geometry = new BufferGeometry();

        if (object instanceof Group) {
            const child1 = <Mesh>object.children[0];
            const child2 = <Mesh>object.children[1];
            const array = [<BufferGeometry>child1.geometry,
                           <BufferGeometry>child2.geometry];
            geometry = BufferGeometryUtils.mergeBufferGeometries(array);
        } else {
            geometry = <BufferGeometry>object.geometry;
        }
        return geometry;
    }

    private computeFaceNeighbours(geometry: Geometry) {
        // if (!this.containerService.faceNeighbours && !this.faceNeighboursComputation) {
        this.faceNeighboursComputation = true;

        this.modelOperationsService.computeFaceNeighbours(geometry).then((value) => {
            this.containerService.faceNeighbours = value;
            this.faceNeighboursComputation = false;
            this.containerService.loadingStatus = 1;
            this.loaded.next(2); // state 2 computeFaceNeighbours is finished
        });
        // }
    }

    private addModel(bufferGeometry: BufferGeometry) {
        delete bufferGeometry.attributes.color;
        const geometry = new Geometry().fromBufferGeometry(bufferGeometry);

        const result = this.modelOperationsService.computeMorphTargets(bufferGeometry, geometry);
        this.containerService.targets = result.targets;
        // initialize targetInfluences only one time
        if (this.containerService.targetInfluences === undefined) {
            this.containerService.targetInfluences = result.influences;
        }

        this.modelOperationsService.normalizeGeometry(geometry);

        const model = new Mesh(geometry, this.containerService.material);
        this.containerService.material.needsUpdate = true;
        // this.scaleModel(model);
        this.containerService.targetModel = new Mesh(geometry.clone(), this.containerService.material);
        this.containerService.renderGroup.add(model);

        const geometry2 = <Geometry>geometry.clone();
        this.modelOperationsService.normalizeGeometry(geometry2);
        this.textureSizeService.initColors(geometry2);

        const colorCodingMesh = new Mesh(geometry2, new MeshBasicMaterial({vertexColors: true, morphTargets: true}));
        // this.scaleModel(colorCodingMesh);
        this.containerService.colorCodingTargetModel = new Mesh(geometry2.clone(),
                                                                new MeshBasicMaterial({vertexColors: true, morphTargets: true}));
        // this.scaleModel(this.containerService.colorCodingTargetModel);
        this.containerService.polyPaintGroup.add(colorCodingMesh);

        this.computeTargetOffsets(result.arrayTargets);
    }

    private computeTargetOffsets(targets) {
        this.containerService.targetOffsets = [];

        this.modelOperationsService.computeTargetOffsets(targets, <Mesh>this.containerService.polyPaintGroup.children[0])
            .then((value) => {
                const offsets = [];
                value.forEach((offset) => {
                    offsets.push(new Vector3().fromArray(offset));
                });
                this.containerService.targetOffsets = offsets;
                this.loaded.next(1);  // state 1 when computeTargetOffsets is finished
            });
    }

    private loadGltfModel(reload: boolean) {
        const status = reload ? 0.9 : 1;

        if (this.modelDataService.type) {
            const model = this.modelDataService.type.toString();
            this.loadGltfModelFromUrl('./assets/Human/Models/' + model + '.gltf', null, status);
        } else {
            this.loadGltfModelFromUrl('./assets/Human/Models/female_adult.gltf', null, status);
        }

    }

    private initModel(reload: boolean) {
        const img = new Image();
        img.src = './assets/Human/Textures/skin.png';

        const ctx = this.containerService.textureCanvas.getContext('2d');

        img.onload = () => {
            if (reload) {
                this.containerService.textureCanvas.width = img.width;
                this.containerService.textureCanvas.height = img.height;
                ctx.drawImage(img, 0, 0);

                this.containerService.texture = new Texture(this.containerService.textureCanvas);
                const textureColorcoding = new TextureLoader().load('./assets/Human/Textures/ColorCoding.png');
                // this.containerService.texture.magFilter = NearestFilter;
                // this.containerService.texture.minFilter = NearestFilter;
                textureColorcoding.magFilter = NearestFilter;
                textureColorcoding.minFilter = NearestFilter;


                this.containerService.material = new MeshPhongMaterial({
                    map: this.containerService.texture,
                    vertexColors: true,
                    morphTargets: true,
                    flatShading: false,
                    side: DoubleSide
                });

                this.containerService.texture.needsUpdate = true;
                this.containerService.materialTexture = <MeshBasicMaterial>new MeshBasicMaterial({
                    map: textureColorcoding,
                    vertexColors: true,
                    morphTargets: true,
                    side: DoubleSide
                });

                this.containerService.material.name = 'material1';
                this.containerService.materialTexture.name = 'material2';
            }


            this.loadGltfModel(reload); // file format: gltf
        };
    }
}
